import type { SchematicNetwork, SchematicNode, SchematicNodeKind } from '@ghp/domain'
import { SchematicShape, createSchematicNode, isSeparateNetworkKind } from '@ghp/schematic'
import { SectionLabel, SegmentedControl } from '@ghp/ui'
import {
  ArrowUpRight,
  Circle,
  Eraser,
  Hand,
  House,
  Minus,
  MousePointer2,
  Pencil,
  Square,
  Tent,
  Type,
  type LucideIcon,
} from 'lucide-react'

import {
  SCHEMATIC_CATALOGUE,
  SCHEMATIC_LEVEL_OPTIONS,
  SCHEMATIC_NETWORK_OPTIONS,
  SCHEMATIC_STRUCTURE_NETWORK_OPTIONS,
  SCHEMATIC_TOOLS,
  SCHEMATIC_TOOL_CAPTIONS,
  SCHEMATIC_TOOL_LABELS,
  type CatalogueFamily,
  type CatalogueItem,
  type SchematicTool,
} from './schematic-labels'
import s from './SchematicEditor.module.css'

/** Le réseau choisi dans la palette : un des trois, ou « Neutre » pour un ouvrage. */
export type PaletteNetwork = SchematicNetwork | 'ink'

export interface SchematicPaletteProps {
  tool: string
  family: CatalogueFamily['value']
  network: PaletteNetwork
  level: string
  disabled?: boolean
  onTool: (tool: string) => void
  onFamily: (family: CatalogueFamily['value']) => void
  onNetwork: (network: PaletteNetwork) => void
  onLevel: (level: string) => void
}

const TOOL_ICONS: Readonly<Record<SchematicTool, LucideIcon>> = {
  select: MousePointer2,
  pan: Hand,
  freehand: Pencil,
  eraser: Eraser,
  polygon: House,
  shelter: Tent,
  line: Minus,
  arrow: ArrowUpRight,
  rectangle: Square,
  ellipse: Circle,
  text: Type,
}

/**
 * La colonne de gauche de l'éditeur : les outils, puis le catalogue de GHP DIGIT en quatre
 * familles (frames Terrain 1.9b → 1.9g).
 *
 * ⚠️ **Un seul sélecteur de réseau** colore tout ce qui se pose : GHP DIGIT doublait chaque
 * ouvrage — « Tampon EU », « Tampon EP » —, soit dix boutons de plus pour la même forme.
 */
export function SchematicPalette({
  tool,
  family,
  network,
  level,
  disabled,
  onTool,
  onFamily,
  onNetwork,
  onLevel,
}: SchematicPaletteProps) {
  const current = SCHEMATIC_CATALOGUE.find((candidate) => candidate.value === family)
  const combinedChosen = family === 'networks' && network === 'combined'
  const stormwaterMissed = family === 'networks' && network !== 'stormwater'

  return (
    <div className={s.palette}>
      <SectionLabel>Outils</SectionLabel>
      {/* Deux groupes nommés, et non un seul qui les engloberait : « Outils » n'est pas le nom
          des éléments, et un lecteur d'écran annoncerait alors « Regard, groupe Outils ». */}
      <div className={s.tools} role="group" aria-label="Outils">
        {SCHEMATIC_TOOLS.map((value) => {
          const Icon = TOOL_ICONS[value]
          return (
            <button
              key={value}
              type="button"
              className={s.tool}
              data-captioned=""
              aria-label={SCHEMATIC_TOOL_LABELS[value]}
              aria-pressed={tool === value}
              disabled={disabled}
              onClick={() => onTool(value)}
            >
              <Icon size={20} aria-hidden />
              <span aria-hidden>{SCHEMATIC_TOOL_CAPTIONS[value]}</span>
            </button>
          )
        })}
      </div>

      <SectionLabel>Éléments</SectionLabel>
      <div className={s.families} role="group" aria-label="Familles d’éléments">
        {SCHEMATIC_CATALOGUE.map((candidate) => (
          <button
            key={candidate.value}
            type="button"
            className={s.family}
            aria-pressed={candidate.value === family}
            disabled={disabled}
            onClick={() => onFamily(candidate.value)}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      {current?.typed === true && (
        <SegmentedControl<PaletteNetwork>
          className={s.switch}
          label="Réseau"
          options={
            family === 'structures'
              ? SCHEMATIC_STRUCTURE_NETWORK_OPTIONS
              : SCHEMATIC_NETWORK_OPTIONS
          }
          value={network}
          disabled={disabled}
          onValueChange={onNetwork}
        />
      )}
      {current?.level === true && (
        <SegmentedControl
          className={s.switch}
          label="Étage"
          options={SCHEMATIC_LEVEL_OPTIONS}
          value={level}
          disabled={disabled}
          onValueChange={onLevel}
        />
      )}

      {current !== undefined && (
        <div
          className={s.items}
          data-wide={family === 'outlets' ? '' : undefined}
          role="group"
          aria-label={current.label}
        >
          {current.items.map((item) => {
            const blocked =
              (combinedChosen && isSeparateOnly(item)) ||
              (stormwaterMissed && isStormwaterOnly(item))
            return (
              <button
                key={item.id}
                type="button"
                className={s.item}
                aria-label={itemLabel(item, network)}
                aria-pressed={tool === item.id}
                disabled={disabled === true || blocked}
                onClick={() => onTool(item.id)}
              >
                {item.text !== undefined ? (
                  <span className={s.itemTag} data-long={item.text.length > 8 ? '' : undefined}>
                    {item.text}
                  </span>
                ) : (
                  <>
                    <ItemGlyph item={item} network={network} />
                    <span aria-hidden>{item.label}</span>
                  </>
                )}
              </button>
            )
          })}
        </div>
      )}

      {/* Un bouton grisé et muet se lit comme une panne (M21, #324) : la raison est écrite. */}
      {combinedChosen && <p className={s.why}>Conduite et Caniveau grisés : ils sont EU ou EP.</p>}
      {stormwaterMissed && (
        <p className={s.why}>Gouttière et Gouttière sur sol grisées : elles sont EP.</p>
      )}
      {current?.level === true && (
        <p className={s.why}>L’étage choisi s’écrit sur chaque étiquette posée : « wc (−1) ».</p>
      )}
    </div>
  )
}

/** Le bouton pose-t-il un élément qui n'est qu'EU ou EP — la conduite privée, le caniveau ? */
export function isSeparateOnly(item: CatalogueItem): boolean {
  return item.kind === 'pipe' || isSeparateNetworkKind(item.kind)
}

/**
 * Le bouton pose-t-il une gouttière, sur sol comprise ? Elle est le départ du réseau EP et ne se
 * pose qu'en EP (« Gouttière pas dans EU ? », point GHP du 21/10, frame Terrain 1.9s).
 */
export function isStormwaterOnly(item: CatalogueItem): boolean {
  return item.kind === 'downpipe' || item.kind === 'downpipeOutfall'
}

/**
 * Le nom d'un bouton du catalogue. La conduite porte son réseau dans son nom, parce que c'est
 * lui qui décide de ce qu'elle pose : une « Conduite EU » et une « Conduite EP » sont deux
 * éléments différents du document.
 */
function itemLabel(item: CatalogueItem, network: PaletteNetwork): string {
  if (item.kind !== 'pipe') return item.label
  return `${item.label} ${network === 'stormwater' ? 'EP' : 'EU'}`
}

/** Le symbole d'un bouton, dessiné par le même rendu que le plan, à la couleur du réseau choisi. */
function ItemGlyph({ item, network }: { item: CatalogueItem; network: PaletteNetwork }) {
  const node = glyphNode(item, network)
  const small =
    node.points.length === 1 && node.kind !== 'manholeCover' && node.kind !== 'septicTank'
  return (
    <svg className={s.glyph} viewBox={glyphView(node, small)} aria-hidden>
      <SchematicShape node={node} selected={false} />
    </svg>
  )
}

/** Le V de la gouttière sur sol et ses tirets demandent plus de place que les autres symboles. */
function glyphView(node: SchematicNode, small: boolean): string {
  if (node.kind === 'downpipeOutfall') return '-40 -40 80 80'
  return small ? '-20 -20 40 40' : '-30 -30 60 60'
}

function glyphNode(item: CatalogueItem, network: PaletteNetwork): SchematicNode {
  const kind = resolvedKind(item, network)
  const line = kind === 'publicNetwork' || kind === 'wastewaterPipe' || kind === 'stormwaterPipe'
  const points =
    kind === 'gratedChannel'
      ? [
          { x: -24, y: 0 },
          { x: 24, y: 0 },
        ]
      : kind === 'downpipeOutfall'
        ? [
            { x: 0, y: -24 },
            { x: 0, y: 36 },
          ]
        : line
          ? [
              { x: -26, y: 0 },
              { x: 18, y: 0 },
            ]
          : [{ x: 0, y: 0 }]

  return typedNode(
    createSchematicNode({
      id: `glyph-${item.id}`,
      kind,
      points,
      stroke: 'ink',
      strokeWidth: kind === 'gratedChannel' || kind === 'downpipeOutfall' ? 'thin' : 'medium',
    }),
    item,
    network,
  )
}

/** Ce qu'un bouton du catalogue pose : la conduite se résout en EU ou en EP selon le réseau choisi. */
export function resolvedKind(item: CatalogueItem, network: PaletteNetwork): SchematicNodeKind {
  if (item.kind === 'pipe') return network === 'stormwater' ? 'stormwaterPipe' : 'wastewaterPipe'
  return item.kind
}

/**
 * Le réseau choisi, appliqué à un élément qui se type. « Neutre » n'est pas un réseau du
 * document : c'est le regard à l'encre de GHP DIGIT, d'où le trait posé à la main.
 */
export function typedNode(
  node: SchematicNode,
  item: CatalogueItem,
  network: PaletteNetwork,
): SchematicNode {
  if (item.typed !== true) return node
  return { ...node, stroke: network === 'ink' ? 'ink' : network }
}
